import {
  ApiAttributionModel,
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import { DesktopWindowsOutlined } from "@mui/icons-material";
import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import CarouselCard from "Components/UI/CarouselCard";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import CustomerValueCohort from "features/analytics/components/CustomerValueCohort";
import { StandardAnalyticsChartStatCard } from "features/analytics";
import {
  CHART_KEY_DASHBOARD_LTV_CAC,
  CHART_KEY_DASHBOARD_LTV_LTV_CAC,
  CHART_KEY_DASHBOARD_LTV_TREND,
} from "features/analytics/constants/chartKeys";
import { useIsMobile } from "Hooks/useIsMobile";
import { useAttributionContext } from "Providers/AttributionProvider";

interface ILtvTabProps {
  attributes: ApiChartAttribute[];
}

function LtvTab({ attributes }: ILtvTabProps) {
  const { model } = useAttributionContext();
  const theme = useTheme();
  const isMobile = useIsMobile();

  return (
    <>
      <CarouselCard
        title="Key Stats"
        emptyMessage="No key stats available"
        items={[
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_LTV_LTV_CAC}
            staticAttributes={attributes}
            chart={{
              name: "LTV:CAC Ratio",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              includePublisherReadyOnly: true,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_LTV_CAC_RATIO],
                },
              ],
              displayOptions: {
                statTitle: "LTV:CAC Ratio",
              },
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_LTV_CAC}
            staticAttributes={attributes}
            chart={{
              name: "New Customer CAC",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              includePublisherReadyOnly: true,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_CAC],
                },
              ],
              displayOptions: {
                statTitle: "New Customer CAC",
              },
              comparison: true,
            }}
          />,
        ]}
        loading={false}
      />

      <Box sx={{ height: 400, flexShrink: 0, width: "100%" }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_LTV_TREND}
          staticAttributes={attributes}
          chart={{
            name: "LTV:CAC Ratio over time",
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [],
            includePublisherReadyOnly: true,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_LTV_CAC_RATIO],
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Box>

      {isMobile ? (
        <Box
          sx={{
            flexShrink: 0,
            width: "100%",
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            p: 2.5,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <DesktopWindowsOutlined
              sx={{ color: "primary.main", mt: 0.25 }}
              fontSize="small"
            />
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                Historical LTV cohorts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The cohort table is best viewed on a larger screen. Stats and
                the LTV:CAC trend above are available here on mobile.
              </Typography>
            </Box>
          </Stack>
        </Box>
      ) : (
        <Box sx={{ minHeight: 600, flexShrink: 0, width: "100%" }}>
          <CustomerValueCohort model={model || ApiAttributionModel.EBA} />
        </Box>
      )}
    </>
  );
}

export default LtvTab;
